import { CheckCircle, XCircle, Clock, Timer } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import Paper from '@/components/Paper'
import { ComplianceOverview } from '@/modules/Service/types/sla'

interface ComplianceSummaryProps {
    overview: ComplianceOverview
    isLoading: boolean
    periodLabel: string
}

const ComplianceSummary = ({
    overview,
    isLoading,
    periodLabel,
}: ComplianceSummaryProps) => {
    if (isLoading) {
        return (
            <div className="space-y-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton key={i} className="h-24" />
                    ))}
                </div>
                <Skeleton className="h-40" />
            </div>
        )
    }

    const totalResponse =
        overview.total_response_met + overview.total_response_breached
    const totalResolution =
        overview.total_resolution_met + overview.total_resolution_breached

    // Raw floats — never Math.round for threshold comparisons
    const responseRaw =
        totalResponse > 0
            ? (overview.total_response_met / totalResponse) * 100
            : 0
    const resolutionRaw =
        totalResolution > 0
            ? (overview.total_resolution_met / totalResolution) * 100
            : 0

    // Hide entire section when there is no response/resolution data —
    // KPI grid above already conveys top-level health.
    if (totalResponse === 0 && totalResolution === 0) {
        return null
    }

    return (
        <div className="space-y-2">
            {/* Response & Resolution cards (KPI duplicates removed — see kpi-grid) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Paper className="border border-gray-100">
                    <div className="pb-2">
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <p className="text-sm font-medium text-foreground">
                                Thời gian Phản hồi · {periodLabel}
                            </p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            Ticket được phản hồi đúng hạn
                        </p>
                    </div>
                    <div className="pt-2">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-2xl font-semibold text-foreground">
                                    {responseRaw.toFixed(1)}%
                                </span>
                                <div className="text-right text-sm">
                                    <span className="text-green-600 font-medium">
                                        {overview.total_response_met}
                                    </span>
                                    <span className="text-gray-400"> / </span>
                                    <span className="text-red-600 font-medium">
                                        {overview.total_response_breached}
                                    </span>
                                </div>
                            </div>
                            <Progress
                                value={Math.min(responseRaw, 100)}
                                className="h-2"
                            />
                            <div className="flex justify-between text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                    Đạt SLA: {overview.total_response_met}
                                </span>
                                <span className="flex items-center gap-1">
                                    <XCircle className="w-3 h-3 text-red-500" />
                                    Vi phạm SLA:{' '}
                                    {overview.total_response_breached}
                                </span>
                            </div>
                        </div>
                    </div>
                </Paper>

                <Paper className="border border-gray-100">
                    <div className="pb-2">
                        <div className="flex items-center gap-2">
                            <Timer className="w-4 h-4 text-muted-foreground" />
                            <p className="text-sm font-medium text-foreground">
                                Thời gian Xử lý · {periodLabel}
                            </p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                            Ticket được xử lý đúng hạn
                        </p>
                    </div>
                    <div className="pt-2">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-2xl font-semibold text-foreground">
                                    {resolutionRaw.toFixed(1)}%
                                </span>
                                <div className="text-right text-sm">
                                    <span className="text-green-600 font-medium">
                                        {overview.total_resolution_met}
                                    </span>
                                    <span className="text-gray-400"> / </span>
                                    <span className="text-red-600 font-medium">
                                        {overview.total_resolution_breached}
                                    </span>
                                </div>
                            </div>
                            <Progress
                                value={Math.min(resolutionRaw, 100)}
                                className="h-2"
                            />
                            <div className="flex justify-between text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                    Đạt SLA: {overview.total_resolution_met}
                                </span>
                                <span className="flex items-center gap-1">
                                    <XCircle className="w-3 h-3 text-red-500" />
                                    Vi phạm SLA:{' '}
                                    {overview.total_resolution_breached}
                                </span>
                            </div>
                        </div>
                    </div>
                </Paper>
            </div>
        </div>
    )
}

export default ComplianceSummary
